import { sweepEffects } from '../effects/gc.js';
import { canonicalCellOrder, compareCellIds, opposing } from '../state/board.js';
import { normalizeZero } from '../state/num.js';
import { rngPick } from '../rng/rng.js';
import { attackProhibitedForSide } from '../sandbox/rules.js';
import { effectiveStats } from '../stats/effectiveStats.js';
import { buildCardContext } from '../ctx/context.js';
import { interpretOp } from '../ops/interpretOp.js';
import { isLiveUnit } from '../registry/subscribers.js';
import { applyAlliedAttackSupporters, applyAttackInterceptors, applyDamageReplacement, applyDeathReplacement, applyIncomingDamageInterceptors, applyIncomingDamageModifiers, applyOwnAttackPreparation, applyOutgoingDamageModifiers, applyRandomDamageReplacement, } from '../registry/replacements.js';
import { startResolutionWorkflow, } from '../registry/resolution.js';
import { buildPureContext } from '../ctx/context.js';
import { hasUnitAttackPermission, ignoresSummoningDelay } from './attackPermissions.js';
import { planShieldMitigation } from './shields.js';
import { planReactionTriggerInstructions, } from './triggerPlanner.js';
function exchangesFor(attackerCells) {
    const eligibleCells = new Set(attackerCells.values());
    const exchanges = [];
    for (const cellId of canonicalCellOrder) {
        const oppositeCellId = opposing(cellId);
        if (compareCellIds(cellId, oppositeCellId) >= 0)
            continue;
        if (!eligibleCells.has(cellId) && !eligibleCells.has(oppositeCellId))
            continue;
        exchanges.push({ firstCellId: cellId, secondCellId: oppositeCellId });
    }
    return exchanges;
}
function playerOnSide(state, side) {
    const player = Object.values(state.players).find((candidate) => candidate.side === side);
    if (player === undefined)
        throw new Error(`resolveCombat: нет игрока стороны '${side}'`);
    return player.id;
}
function liveUnitsCanonical(state) {
    const result = [];
    for (const cellId of canonicalCellOrder) {
        const cell = state.board.cells[cellId];
        const instanceId = cell?.occupantId;
        if (instanceId === null || instanceId === undefined)
            continue;
        const instance = state.instances[instanceId];
        if (instance === undefined || !isLiveUnit(state, instance))
            continue;
        result.push({
            instanceId,
            cellId,
            seq: instance.seq,
            attack: effectiveStats(state, instanceId).attack,
        });
    }
    return result;
}
function orchestrationHooks(state, units, hookName) {
    const hooks = units.map((unit) => ({
        subject: { selfId: unit.instanceId },
        defId: state.instances[unit.instanceId]?.defId ?? '',
        hookName,
        cellId: unit.cellId,
        seq: unit.seq,
    }));
    for (const effect of state.effects) {
        const cellId = effect.scope.kind === 'cell'
            ? effect.scope.cellId
            : effect.scope.kind === 'unit'
                ? state.instances[effect.scope.instanceId]?.cellId
                : undefined;
        hooks.push({
            subject: { effectId: effect.id },
            defId: effect.defId,
            hookName,
            ...(cellId !== undefined ? { cellId } : {}),
            seq: effect.seq,
        });
    }
    return hooks;
}
function sameDamageTarget(left, right) {
    return left.kind === right.kind
        && (left.kind === 'unit'
            ? left.instanceId === (right.kind === 'unit' ? right.instanceId : undefined)
            : left.playerId === (right.kind === 'player' ? right.playerId : undefined));
}
/**
 * Movement during synchronous attack interception settles before damage.
 * Non-cancelled positional groups therefore follow the final opposing cells of
 * their attackers. Explicit Hack/replacement targets and the avoided group
 * that caused movement remain fixed.
 */
function retargetPositionalIntents(state, intents) {
    for (const intent of intents) {
        if (!intent.tracksFinalPosition || intent.avoided || intent.instruction === null)
            continue;
        const attacker = state.instances[intent.attackerId];
        if (attacker?.cellId === undefined || !isLiveUnit(state, attacker))
            continue;
        const targetCellId = opposing(attacker.cellId);
        const targetCell = state.board.cells[targetCellId];
        if (targetCell === undefined)
            throw new Error(`resolveCombat: нет ячейки '${targetCellId}'`);
        const target = targetCell.occupantId === null
            ? { kind: 'player', playerId: playerOnSide(state, targetCell.side) }
            : { kind: 'unit', instanceId: targetCell.occupantId };
        intent.target = target;
        intent.targetCellId = targetCellId;
        intent.instruction = {
            ...intent.instruction,
            target: structuredClone(target),
            targetCellId,
        };
    }
}
/**
 * Keep combat attribution coherent after a replacement changes target without
 * carrying the old positional hint along. Unit position is authoritative;
 * player damage keeps the selected opposing/hack cell because a player has no
 * unique board cell of its own.
 */
function normalizeCombatTargetCell(state, instruction, selectedTargetCellId) {
    if (instruction.target.kind === 'unit') {
        const target = state.instances[instruction.target.instanceId];
        const cellId = target?.cellId;
        if (target === undefined
            || !isLiveUnit(state, target)
            || cellId === undefined
            || state.board.cells[cellId]?.occupantId !== target.id) {
            throw new Error(`resolveCombat: финальная unit target '${instruction.target.instanceId}' не находится на поле`);
        }
        if (instruction.targetCellId !== undefined && instruction.targetCellId !== cellId) {
            throw new Error(`resolveCombat: targetCellId '${instruction.targetCellId}' не совпадает с финальной unit target '${cellId}'`);
        }
        return { ...instruction, targetCellId: cellId };
    }
    const targetCellId = instruction.targetCellId ?? selectedTargetCellId;
    const targetCell = state.board.cells[targetCellId];
    const targetPlayer = state.players[instruction.target.playerId];
    if (targetCell === undefined || targetPlayer === undefined || targetCell.side !== targetPlayer.side) {
        throw new Error(`resolveCombat: targetCellId '${targetCellId}' не соответствует player target '${instruction.target.playerId}'`);
    }
    return { ...instruction, targetCellId };
}
function hackCandidates(state, attacker) {
    const marker = state.instances[attacker.instanceId]?.hack;
    if (marker === undefined)
        return [];
    const victim = state.players[marker.victimControllerIdAtApply];
    const attackerCell = state.board.cells[attacker.cellId];
    if (victim === undefined || attackerCell === undefined)
        return [];
    const candidates = [{
            target: { kind: 'player', playerId: victim.id },
            targetCellId: attacker.cellId,
        }];
    for (const laneIndex of [attackerCell.laneIndex - 1, attackerCell.laneIndex + 1]) {
        if (laneIndex < 0 || laneIndex > 2)
            continue;
        const neighbor = Object.values(state.board.cells).find((cell) => cell.tableId === attackerCell.tableId && cell.laneIndex === laneIndex && cell.side === victim.side);
        if (neighbor?.occupantId !== null && neighbor?.occupantId !== undefined) {
            candidates.push({
                target: { kind: 'unit', instanceId: neighbor.occupantId },
                targetCellId: neighbor.id,
            });
        }
    }
    return candidates;
}
function buildDamagePlan(ctx, registry, snapshot, reactionCandidates, origin, attackGroupIdOf) {
    const state = ctx.draft;
    const raw = [];
    for (const attacker of snapshot) {
        const attackGroupId = attackGroupIdOf(attacker);
        applyOwnAttackPreparation(registry, state, ctx.ops, {
            attackGroupId,
            attackerId: attacker.instanceId,
            origin,
        });
        // Select the provisional opposing-cell target for interception. After all
        // synchronous interceptors settle movement, non-cancelled positional
        // groups are retargeted from their attackers' final cells below.
        const targetCellId = opposing(attacker.cellId);
        const targetCell = state.board.cells[targetCellId];
        if (targetCell === undefined)
            throw new Error(`resolveCombat: нет ячейки '${targetCellId}'`);
        const normalTarget = targetCell.occupantId === null
            ? { kind: 'player', playerId: playerOnSide(state, targetCell.side) }
            : { kind: 'unit', instanceId: targetCell.occupantId };
        let target = normalTarget;
        let selectedTargetCellId = targetCellId;
        let hack;
        const marker = state.instances[attacker.instanceId]?.hack;
        if (marker !== undefined) {
            const candidates = hackCandidates(state, attacker);
            if (candidates.length > 0) {
                const [selected, nextRng] = rngPick(state.rng, candidates);
                state.rng = nextRng;
                target = selected.target;
                selectedTargetCellId = selected.targetCellId ?? targetCellId;
                hack = {
                    hackId: marker.hackId,
                    initiatorPlayerId: marker.initiatorPlayerId,
                    candidates,
                    selected,
                };
            }
        }
        // Preparation is deliberately before the per-group stat snapshot so an
        // authored temporary attack modifier affects this exact group.
        const base = normalizeZero(effectiveStats(state, attacker.instanceId).attack);
        const outgoing = applyOutgoingDamageModifiers(registry, state, {
            sourceId: attacker.instanceId,
            origin,
            target,
            targetCellId: selectedTargetCellId,
            base,
            amount: base,
        });
        const replaced = applyDamageReplacement(registry, state, outgoing);
        const randomized = replaced === null
            ? { instruction: null }
            : applyRandomDamageReplacement(registry, state, replaced);
        const tracksFinalPosition = hack === undefined
            && randomized.instruction !== null
            && sameDamageTarget(randomized.instruction.target, normalTarget)
            && (randomized.instruction.targetCellId ?? selectedTargetCellId) === targetCellId;
        let instruction = randomized.instruction === null
            ? null
            : normalizeCombatTargetCell(state, randomized.instruction, selectedTargetCellId);
        const finalTarget = instruction?.target ?? target;
        const finalTargetCellId = instruction?.targetCellId ?? selectedTargetCellId;
        let avoided = false;
        // Supporters react to the attack intent even when a deterministic/random
        // replacement prevents its damage completely. AttackResolved/AttackPerformed
        // still publish that attack; for a cancelled instruction its observable
        // target remains the pre-replacement target used by those events.
        const attackGroup = {
            ...(instruction ?? outgoing),
            attackGroupId,
            attackerId: attacker.instanceId,
        };
        applyAlliedAttackSupporters(registry, state, ctx.ops, attackGroup);
        if (instruction !== null) {
            const interception = applyAttackInterceptors(registry, state, ctx.ops, {
                ...attackGroup,
            });
            for (const check of interception.checks) {
                ctx.emit({
                    type: 'AttackAvoidanceChecked',
                    attackGroupId,
                    attackerId: attacker.instanceId,
                    target: structuredClone(instruction.target),
                    targetCellId: instruction.targetCellId ?? selectedTargetCellId,
                    ...(check.sourceId !== undefined ? { sourceId: check.sourceId } : {}),
                    abilityId: check.abilityId,
                    succeeded: check.succeeded,
                });
            }
            avoided = interception.avoided;
            if (avoided)
                instruction = null;
        }
        raw.push({
            attackGroupId,
            attackerId: attacker.instanceId,
            target: finalTarget,
            targetCellId: finalTargetCellId,
            baseAmount: base,
            instruction,
            avoided,
            tracksFinalPosition,
            ...(randomized.event !== undefined ? { randomReplacementEvent: randomized.event } : {}),
            ...(hack !== undefined ? { hack } : {}),
        });
    }
    retargetPositionalIntents(state, raw);
    // Generic replace/null уже завершён. Только оставшиеся финальные инструкции
    // участвуют в reservation Реакций; dense index затем маппится обратно в intent.
    const finalInstructions = raw.flatMap((intent) => intent.instruction === null ? [] : [intent.instruction]);
    const reactions = planReactionTriggerInstructions(finalInstructions, reactionCandidates);
    const reactionByInstruction = new Map(reactions.map((reaction) => [reaction.damageInstructionIndex, reaction]));
    let reactionIndex = 0;
    const reacted = raw.map((intent) => {
        if (intent.instruction === null)
            return intent;
        const reaction = reactionByInstruction.get(reactionIndex++);
        if (reaction === undefined)
            return intent;
        const replacement = normalizeCombatTargetCell(state, structuredClone(reaction.replacement), intent.targetCellId);
        return {
            ...intent,
            instruction: replacement,
            reaction: { ...reaction, replacement },
        };
    });
    return reacted;
}
function armedReactionCandidates(registry, state) {
    return Object.values(state.players)
        .flatMap((player) => player.armedReactions)
        .map((reactionInstanceId) => state.instances[reactionInstanceId])
        .filter((instance) => instance !== undefined && instance.zone === 'armed')
        .sort((a, b) => a.seq - b.seq)
        .flatMap((instance) => {
        const planner = registry.get(instance.defId)?.hooks?.planReactionTrigger;
        if (planner === undefined)
            return [];
        return [{
                reactionInstanceId: instance.id,
                seq: instance.seq,
                plan: (instruction, reservedReactionIds) => planner(buildPureContext(state, { selfId: instance.id }, registry), instruction, reservedReactionIds),
            }];
    });
}
function splitDamage(total, count) {
    const normalized = normalizeZero(Math.max(0, Math.floor(total)));
    const quotient = Math.floor(normalized / count);
    const remainder = normalized % count;
    return Array.from({ length: count }, (_, index) => quotient + (index < remainder ? 1 : 0));
}
function prepareAttackGroup(ctx, registry, intent) {
    const instruction = intent.instruction;
    const target = instruction?.target ?? intent.target;
    const targetCellId = instruction?.targetCellId ?? intent.targetCellId;
    if (instruction === null)
        return { intent, target, targetCellId, overflowToPlayer: false, parts: [] };
    const attacker = ctx.draft.instances[intent.attackerId];
    const definition = attacker === undefined ? undefined : registry.get(attacker.defId);
    const partCount = target.kind === 'player' ? 1 : definition?.combatDamageParts ?? 1;
    const overflowToPlayer = target.kind === 'unit' && definition?.combatDamageOverflow === 'to_player';
    const split = splitDamage(instruction.amount, partCount);
    const parts = split.map((baseAmount, partIndex) => {
        // A zero produced by group splitting is visual summary only: it must not
        // consume stateful incoming interceptors or emit DamageDealt.
        if (baseAmount === 0 && partCount > 1)
            return { partIndex, partCount, baseAmount };
        const planned = {
            ...(instruction.sourceId !== undefined ? { sourceId: instruction.sourceId } : {}),
            origin: instruction.origin,
            target: structuredClone(target),
            targetCellId,
            base: baseAmount,
            amount: baseAmount,
            attackGroupId: intent.attackGroupId,
            attackerId: intent.attackerId,
            partIndex,
            partCount,
        };
        // Overflow must defer target-side hooks until this part is actually due:
        // a prior part can destroy the unit and route this one to the player.
        if (overflowToPlayer)
            return { partIndex, partCount, baseAmount, instruction: planned };
        const incoming = applyIncomingDamageModifiers(registry, ctx.draft, planned);
        const interception = applyIncomingDamageInterceptors(registry, ctx.draft, ctx.ops, incoming);
        return {
            partIndex,
            partCount,
            baseAmount,
            instruction: interception.instruction,
            ...(interception.transfers.length > 0 ? { transfers: interception.transfers } : {}),
        };
    });
    return { intent, target, targetCellId, overflowToPlayer, parts };
}
function emitDamage(ctx, instruction, absorbed) {
    let lethal = false;
    let targetDefId;
    let targetControllerId;
    if (instruction.target.kind === 'unit') {
        const target = ctx.draft.instances[instruction.target.instanceId];
        if (target === undefined) {
            throw new Error(`resolveCombat: цель урона '${instruction.target.instanceId}' не существует`);
        }
        targetDefId = target.defId;
        targetControllerId = target.controllerId;
        const before = effectiveStats(ctx.draft, instruction.target.instanceId).health;
        lethal = before > 0 && before - instruction.amount <= 0;
    }
    const event = {
        type: 'DamageDealt',
        ...(instruction.sourceId !== undefined ? { sourceId: instruction.sourceId } : {}),
        origin: instruction.origin,
        target: instruction.target,
        ...(instruction.targetCellId !== undefined ? { targetCellId: instruction.targetCellId } : {}),
        ...(targetDefId !== undefined ? { targetDefId } : {}),
        ...(targetControllerId !== undefined ? { targetControllerId } : {}),
        ...(instruction.base !== instruction.amount + absorbed ? { baseAmount: instruction.base } : {}),
        amount: instruction.amount,
        ...(absorbed > 0 ? { absorbed } : {}),
        ...(lethal ? { lethal: true } : {}),
        combat: true,
        attackGroupId: instruction.attackGroupId,
        partIndex: instruction.partIndex,
        partCount: instruction.partCount,
    };
    ctx.emit(event);
}
function redirectOverflowToPlayer(state, instruction) {
    if (instruction.target.kind !== 'unit')
        return instruction;
    const target = state.instances[instruction.target.instanceId];
    if (target === undefined || effectiveStats(state, target.id).health > 0)
        return instruction;
    const targetCellId = instruction.targetCellId;
    if (targetCellId === undefined)
        throw new Error('resolveCombat: overflow part has no targetCellId');
    const targetCell = state.board.cells[targetCellId];
    if (targetCell === undefined)
        throw new Error(`resolveCombat: нет ячейки '${targetCellId}'`);
    return {
        ...instruction,
        target: { kind: 'player', playerId: playerOnSide(state, targetCell.side) },
    };
}
/**
 * A multi-part strike is a sequence of simultaneous waves, not an indelible
 * list of posthumous packets.  Each wave snapshots which attackers are still
 * present with positive health; all of those sources deal that numbered part,
 * then the next wave rechecks their liveness.  Thus two facing cards exchange
 * their first parts simultaneously, while a source killed by that exchange
 * cannot deliver a later part.
 */
function canResolveAttackPart(state, attackerId) {
    const attacker = state.instances[attackerId];
    return attacker !== undefined
        && isLiveUnit(state, attacker)
        && effectiveStats(state, attacker.id).health > 0;
}
function resolvePreparedAttackPart(ctx, registry, group, part) {
    if (part.instruction === undefined) {
        return {
            partIndex: part.partIndex,
            partCount: part.partCount,
            baseAmount: part.baseAmount,
            amount: 0,
        };
    }
    let instruction = part.instruction;
    if (group.overflowToPlayer) {
        instruction = redirectOverflowToPlayer(ctx.draft, instruction);
        const incoming = applyIncomingDamageModifiers(registry, ctx.draft, instruction);
        const interception = applyIncomingDamageInterceptors(registry, ctx.draft, ctx.ops, incoming);
        instruction = interception.instruction;
        if (interception.transfers.length > 0)
            part.transfers = interception.transfers;
    }
    const shielded = planShieldMitigation(ctx.draft, [instruction])[0];
    for (const consumption of shielded.consumptions) {
        ctx.emit({
            type: 'ShieldAbsorbed', effectId: consumption.effectId,
            sourceInstanceId: consumption.sourceInstanceId,
            targetInstanceId: consumption.targetInstanceId,
            amount: consumption.amount, remaining: consumption.remaining,
        });
        if (consumption.remaining === 0) {
            ctx.emit({
                type: 'ShieldDepleted', effectId: consumption.effectId,
                sourceInstanceId: consumption.sourceInstanceId,
                targetInstanceId: consumption.targetInstanceId,
            });
            if (consumption.onDepletedCool !== undefined) {
                ctx.ops.cool(consumption.targetInstanceId, consumption.onDepletedCool);
            }
            ctx.ops.removeEffect(consumption.effectId);
        }
    }
    const finalInstruction = {
        ...shielded.instruction,
        amount: normalizeZero(Math.max(0, Math.floor(shielded.instruction.amount))),
    };
    emitDamage(ctx, finalInstruction, shielded.absorbed);
    return {
        partIndex: part.partIndex,
        partCount: part.partCount,
        baseAmount: part.baseAmount,
        amount: finalInstruction.amount,
        ...(shielded.absorbed > 0 ? { absorbed: shielded.absorbed } : {}),
    };
}
function runHackAttachmentHooks(ctx, registry, event) {
    const attachmentIds = [...ctx.draft.attachments]
        .sort((a, b) => {
        const aCell = ctx.draft.instances[a.hostInstanceId]?.cellId ?? '';
        const bCell = ctx.draft.instances[b.hostInstanceId]?.cellId ?? '';
        return aCell.localeCompare(bCell) || a.seq - b.seq;
    })
        .map((attachment) => attachment.attachmentId);
    for (const attachmentId of attachmentIds) {
        const attachment = ctx.draft.attachments.find((candidate) => candidate.attachmentId === attachmentId);
        if (attachment === undefined)
            continue;
        const hook = registry.get(attachment.defId)?.hooks?.onHackTriggered;
        if (hook === undefined)
            continue;
        hook(buildCardContext(ctx.draft, ctx.ops, { attachmentId }, undefined, registry), event);
    }
}
function applyAttackPlan(ctx, registry, plan) {
    for (const intent of plan) {
        if (intent.hack === undefined)
            continue;
        const event = {
            type: 'HackTriggered', hackId: intent.hack.hackId,
            victimInstanceId: intent.attackerId, attackerId: intent.attackerId,
            initiatorPlayerId: intent.hack.initiatorPlayerId,
            candidates: structuredClone(intent.hack.candidates),
            selected: structuredClone(intent.hack.selected),
        };
        ctx.emit(event);
        runHackAttachmentHooks(ctx, registry, event);
        ctx.ops.removeHack(intent.attackerId, 'triggered');
    }
    for (const intent of plan) {
        if (intent.randomReplacementEvent !== undefined)
            ctx.emit(intent.randomReplacementEvent);
    }
    // Реакции уже зарезервированы чистым planner-ом. Их публичные события и
    // отложенные side effects исполняются до общего Shield snapshot: такой side
    // effect, как dealDamage, может сам израсходовать или удалить Щит.
    for (const intent of plan) {
        if (intent.reaction !== undefined) {
            const reaction = ctx.draft.instances[intent.reaction.reactionInstanceId];
            if (reaction !== undefined && reaction.zone === 'armed') {
                ctx.emit({ type: 'CardRevealed', playerId: reaction.ownerId, instanceId: reaction.id, kind: 'REACTION' });
                ctx.emit({
                    type: 'ReactionTriggered', playerId: reaction.ownerId, instanceId: reaction.id,
                    damageInstructionIndex: intent.reaction.damageInstructionIndex,
                    ...(intent.reaction.replacement.sourceId !== undefined ? { sourceId: intent.reaction.replacement.sourceId } : {}),
                    target: structuredClone(intent.reaction.replacement.target),
                    amount: intent.reaction.replacement.amount,
                });
                ctx.emit({ type: 'CardMoved', instanceId: reaction.id, from: 'armed', to: 'discard' });
            }
            for (const op of intent.reaction.deferredSideEffects)
                interpretOp(ctx.ops, op);
        }
    }
    // Group totals have now passed final-target replacement, attack interception
    // and one Reaction reservation. Split only field-card targets; player damage
    // deliberately stays one packet.  A numbered part from each opposing group
    // shares a liveness snapshot; later parts see the health left by that wave.
    // Shields still use the live remainder, and an overflow part can therefore
    // retarget after an earlier part destroyed its target.
    const prepared = plan.map((intent) => prepareAttackGroup(ctx, registry, intent));
    const summariesByGroup = prepared.map(() => []);
    const partWaves = Math.max(0, ...prepared.map((group) => group.parts.length));
    for (let partIndex = 0; partIndex < partWaves; partIndex += 1) {
        const liveSources = new Set(prepared.flatMap((group) => {
            const part = group.parts[partIndex];
            return part?.instruction !== undefined && canResolveAttackPart(ctx.draft, group.intent.attackerId)
                ? [group.intent.attackerId]
                : [];
        }));
        for (const [groupIndex, group] of prepared.entries()) {
            const part = group.parts[partIndex];
            if (part === undefined)
                continue;
            const summaries = summariesByGroup[groupIndex];
            if (part.instruction === undefined || !liveSources.has(group.intent.attackerId)) {
                summaries.push({
                    partIndex: part.partIndex,
                    partCount: part.partCount,
                    baseAmount: part.baseAmount,
                    amount: 0,
                });
                continue;
            }
            summaries.push(resolvePreparedAttackPart(ctx, registry, group, part));
        }
    }
    for (const [groupIndex, group] of prepared.entries()) {
        const summaries = summariesByGroup[groupIndex];
        const amount = summaries.reduce((total, part) => total + part.amount, 0);
        const absorbed = summaries.reduce((total, part) => total + (part.absorbed ?? 0), 0);
        ctx.emit({
            type: 'AttackResolved',
            attackGroupId: group.intent.attackGroupId,
            attackerId: group.intent.attackerId,
            target: structuredClone(group.target),
            targetCellId: group.targetCellId,
            baseAmount: group.intent.baseAmount,
            amount,
            ...(absorbed > 0 ? { absorbed } : {}),
            ...(group.intent.avoided ? { avoided: true } : {}),
            parts: summaries,
        });
        ctx.emit({
            type: 'AttackPerformed', attackerId: group.intent.attackerId,
            target: structuredClone(group.target),
            targetCellId: group.targetCellId,
        });
    }
    // Переносы перехваченного урона исполняются ПОСЛЕ двух групп текущей
    // одновременной пары (все части сняты с одного снимка) и ДО её death-wave: погибший
    // поглотитель попадает в ту же волну. Получатель, уже покинувший поле,
    // детерминированно пропускается.
    for (const group of prepared) {
        for (const part of group.parts) {
            for (const transfer of part.transfers ?? []) {
                const receiver = ctx.draft.instances[transfer.instanceId];
                if (receiver === undefined || receiver.zone !== 'board' || receiver.cellId === undefined
                    || ctx.draft.board.cells[receiver.cellId]?.occupantId !== receiver.id)
                    continue;
                ctx.ops.dealDamage({ kind: 'unit', instanceId: transfer.instanceId }, transfer.amount, transfer.sourceId);
            }
        }
    }
}
/** Отдельная атака использует тот же target/reaction/shield package, не трогая право обычного боя. */
export function resolveExtraAttackRequest(ctx, registry, requestId, attackerId) {
    const instance = ctx.draft.instances[attackerId];
    if (instance === undefined || instance.cellId === undefined || !canUnitAttack(ctx.draft, registry, attackerId)) {
        ctx.emit({ type: 'ExtraAttackResolved', requestId, attackerId, performed: false });
        return false;
    }
    const snapshot = {
        instanceId: instance.id, cellId: instance.cellId, seq: instance.seq,
        attack: effectiveStats(ctx.draft, instance.id).attack,
    };
    const plan = buildDamagePlan(ctx, registry, [snapshot], armedReactionCandidates(registry, ctx.draft), 'extra_attack', () => `extra:${requestId}`);
    applyAttackPlan(ctx, registry, plan);
    ctx.emit({ type: 'ExtraAttackResolved', requestId, attackerId, performed: true });
    return true;
}
function canFormAttackIntent(state, definitionType, instanceId) {
    return definitionType !== undefined && (definitionType === 'UNIT' || state.effects.some((effect) => effect.scope.kind === 'unit'
        && effect.scope.instanceId === instanceId
        && effect.vars.attackPermission === 'allow_non_unit'));
}
/** Единый M11-предикат: задержка → Заморозка → прямой запрет → intent. */
export function attackEligibility(state, registry, instanceId) {
    const instance = state.instances[instanceId];
    const definition = instance === undefined ? undefined : registry.get(instance.defId);
    if (instance === undefined || definition === undefined
        || !canFormAttackIntent(state, definition.type, instanceId))
        return 'not_unit';
    if (instance.enteredRound === state.round
        && !definition.keywords?.includes('RUSH')
        && !ignoresSummoningDelay(state, instanceId))
        return 'not_ready';
    const ignoresFreeze = state.effects.some((effect) => effect.scope.kind === 'unit'
        && effect.scope.instanceId === instanceId
        && effect.vars.attackPermission === 'ignore_freeze'
        && effect.vars.round === state.round);
    if ((instance.freeze ?? 0) > 0 && !ignoresFreeze)
        return 'frozen';
    if (definition.keywords?.includes('CANNOT_ATTACK')
        || hasUnitAttackPermission(state, instanceId, 'prohibit'))
        return 'prohibited';
    // Generic pre-declared attack-prohibition RuleFlag (R15/D8): единый eligibility
    // path покрывает ordinary и extra attacks. Обычные матчи флаг не ставят.
    const attackerSide = state.players[instance.controllerId]?.side;
    if (attackerSide !== undefined && attackProhibitedForSide(state.rules, attackerSide))
        return 'prohibited';
    return 'ready';
}
export function canUnitAttack(state, registry, instanceId, options) {
    const instance = state.instances[instanceId];
    if (instance === undefined || effectiveStats(state, instanceId).attack <= 0)
        return false;
    if (options?.ignoreSummoningDelay === true && instance.enteredRound === state.round) {
        const copy = structuredClone(state);
        if (copy.instances[instanceId] !== undefined)
            copy.instances[instanceId].enteredRound = Math.max(0, state.round - 1);
        return attackEligibility(copy, registry, instanceId) === 'ready';
    }
    return attackEligibility(state, registry, instanceId) === 'ready';
}
/**
 * Переиспользуемая возобновляемая волна simultaneous deaths. Она применяет все
 * исходы на удержанной очереди, делает sweep, затем выпускает UnitDied/
 * EffectRemoved к триггерам. Следующий advance происходит только после полного
 * drain (включая choose/cascade).
 */
export class DeathWaveMachine {
    registry;
    deathCause;
    eventsDeferred;
    attempted = new Set();
    constructor(registry, eventsAlreadyDeferred = false, 
    /** Причина Гибели для health≤0 волны: боевая фаза — 'combat', иначе эффектный урон. */
    deathCause = 'damage') {
        this.registry = registry;
        this.deathCause = deathCause;
        this.eventsDeferred = eventsAlreadyDeferred;
    }
    advance(ctx) {
        const deaths = [];
        for (const unit of liveUnitsCanonical(ctx.draft)) {
            const instance = ctx.draft.instances[unit.instanceId];
            if (instance === undefined ||
                effectiveStats(ctx.draft, instance.id).health > 0 ||
                this.attempted.has(instance.id))
                continue;
            deaths.push({
                instanceId: instance.id,
                cellId: unit.cellId,
                isToken: instance.isToken === true,
                replacement: applyDeathReplacement(this.registry, ctx.draft, instance.id),
            });
        }
        if (deaths.length === 0 && !this.eventsDeferred)
            return true;
        if (!this.eventsDeferred) {
            ctx.beginEventDeferral();
            this.eventsDeferred = true;
        }
        // Replacement outcomes были рассчитаны одновременно. Перед исполнением
        // каждого перепроверяем, не уничтожил/переместил/вылечил ли его более ранний
        // replacement в этой же канонической волне.
        for (const death of deaths) {
            const instance = ctx.draft.instances[death.instanceId];
            const cell = ctx.draft.board.cells[death.cellId];
            if (instance === undefined ||
                instance.zone !== 'board' ||
                instance.cellId !== death.cellId ||
                cell?.occupantId !== instance.id ||
                effectiveStats(ctx.draft, instance.id).health > 0)
                continue;
            if (death.replacement === 'prevent') {
                this.attempted.add(death.instanceId);
                continue;
            }
            if (death.replacement !== null) {
                for (const op of death.replacement)
                    interpretOp(ctx.ops, op);
                const after = ctx.draft.instances[death.instanceId];
                if (after !== undefined &&
                    isLiveUnit(ctx.draft, after) &&
                    effectiveStats(ctx.draft, after.id).health <= 0)
                    this.attempted.add(death.instanceId);
                continue;
            }
            for (const attachment of [...ctx.draft.attachments]
                .filter((candidate) => candidate.hostInstanceId === instance.id)
                .sort((a, b) => a.seq - b.seq)) {
                ctx.ops.removeAttachment(attachment.attachmentId, 'host_left');
            }
            const capture = ctx.draft.captureReservations.find((candidate) => candidate.targetInstanceId === instance.id);
            if (capture !== undefined)
                ctx.ops.returnCapture(capture.captureId, 'left');
            ctx.emit({
                type: 'UnitDied', instanceId: death.instanceId, cellId: death.cellId,
                defId: instance.defId, ownerId: instance.ownerId,
                controllerIdAtDeath: instance.controllerId,
                isToken: death.isToken, cause: this.deathCause,
            });
            if (!death.isToken) {
                ctx.emit({ type: 'CardMoved', instanceId: death.instanceId, from: 'board', to: 'discard' });
            }
        }
        // S11: физический sweep до первого реактивного боевого/раундового trigger.
        for (const effectId of sweepEffects(ctx.draft))
            ctx.ops.removeEffect(effectId);
        ctx.flushDeferredEvents();
        this.eventsDeferred = false;
        return false;
    }
}
/** Возобновляемая фазовая машина одного боя (snapshot → plan → deaths → triggers). */
export class CombatPhaseMachine {
    registry;
    reactionCandidates;
    stage = 'start';
    initialParticipants = [];
    deathWave = null;
    damageEventsDeferred = false;
    afterDeathsStage = 'after_hooks';
    combatAttackerCells = new Map();
    exchanges = [];
    nextExchangeIndex = 0;
    constructor(registry, reactionCandidates = (state) => armedReactionCandidates(registry, state)) {
        this.registry = registry;
        this.reactionCandidates = reactionCandidates;
    }
    advance(ctx) {
        switch (this.stage) {
            case 'start': {
                ctx.emit({ type: 'CombatStarted', round: ctx.draft.round });
                this.stage = 'precombat_deaths';
                return false;
            }
            case 'precombat_deaths': {
                const hasDead = liveUnitsCanonical(ctx.draft).some((unit) => {
                    const instance = ctx.draft.instances[unit.instanceId];
                    return instance !== undefined && effectiveStats(ctx.draft, instance.id).health <= 0;
                });
                if (hasDead) {
                    this.afterDeathsStage = 'before_hooks';
                    this.stage = 'deaths';
                }
                else {
                    this.initialParticipants = liveUnitsCanonical(ctx.draft);
                    this.stage = 'before_hooks';
                }
                return false;
            }
            case 'before_hooks': {
                // После возможной pre-combat волны смертей фиксируем именно живых
                // участников, которым разрешено дать onBeforeCombat.
                this.initialParticipants = liveUnitsCanonical(ctx.draft);
                ctx.scheduleHooks(orchestrationHooks(ctx.draft, this.initialParticipants, 'onBeforeCombat'));
                this.stage = 'post_before_deaths';
                return false;
            }
            case 'post_before_deaths': {
                const hasDead = liveUnitsCanonical(ctx.draft).some((unit) => {
                    const instance = ctx.draft.instances[unit.instanceId];
                    return (instance !== undefined &&
                        effectiveStats(ctx.draft, instance.id).health <= 0);
                });
                if (hasDead) {
                    this.afterDeathsStage = 'eligibility';
                    this.stage = 'deaths';
                }
                else {
                    this.stage = 'eligibility';
                }
                return false;
            }
            case 'eligibility': {
                const live = liveUnitsCanonical(ctx.draft);
                this.combatAttackerCells.clear();
                for (const unit of live) {
                    if (attackEligibility(ctx.draft, this.registry, unit.instanceId) === 'ready') {
                        this.combatAttackerCells.set(unit.instanceId, unit.cellId);
                    }
                }
                this.exchanges = exchangesFor(this.combatAttackerCells);
                this.nextExchangeIndex = 0;
                for (const unit of live) {
                    if (attackEligibility(ctx.draft, this.registry, unit.instanceId) !== 'frozen')
                        continue;
                    const previous = ctx.draft.instances[unit.instanceId]?.freeze ?? 0;
                    ctx.emit({
                        type: 'FreezeChanged', instanceId: unit.instanceId,
                        previous, current: Math.max(0, previous - 1), reason: 'combat_skip',
                    });
                }
                this.stage = 'snapshot_and_apply';
                return false;
            }
            case 'snapshot_and_apply': {
                const exchange = this.exchanges[this.nextExchangeIndex];
                if (exchange === undefined) {
                    this.stage = 'after_hooks';
                    return false;
                }
                this.nextExchangeIndex += 1;
                // Eligibility фиксирует только право на этот бой. FreezeChanged и
                // предыдущий размен могут менять состояние, поэтому новая пара читает
                // live snapshot, но только для ранее eligible участников. Новые юниты
                // не получают обычную атаку посреди уже начавшегося боя.
                const combatAttackers = liveUnitsCanonical(ctx.draft).filter((unit) => this.combatAttackerCells.get(unit.instanceId) === unit.cellId
                    && (unit.cellId === exchange.firstCellId || unit.cellId === exchange.secondCellId)
                    && effectiveStats(ctx.draft, unit.instanceId).health > 0);
                if (combatAttackers.length === 0)
                    return false;
                // An exchange is atomic for the two opposing cards, but once its death
                // wave and subscribers drain, the next exchange must build a fresh
                // damage snapshot so reactive stats/HP are real gameplay state.
                this.afterDeathsStage = 'snapshot_and_apply';
                ctx.beginEventDeferral();
                this.damageEventsDeferred = true;
                const plan = buildDamagePlan(ctx, this.registry, combatAttackers, this.reactionCandidates(ctx.draft), 'ordinary_attack', (attacker) => `combat:r${ctx.draft.round}:${attacker.instanceId}`);
                applyAttackPlan(ctx, this.registry, plan);
                this.stage = 'deaths';
                return false;
            }
            case 'deaths': {
                this.deathWave ??= new DeathWaveMachine(this.registry, this.damageEventsDeferred, 'combat');
                this.damageEventsDeferred = false;
                if (this.deathWave.advance(ctx)) {
                    this.deathWave = null;
                    this.stage = this.afterDeathsStage;
                }
                return false;
            }
            case 'after_hooks': {
                ctx.scheduleHooks(orchestrationHooks(ctx.draft, liveUnitsCanonical(ctx.draft), 'onAfterCombat'));
                this.stage = 'after_hooks_check';
                return false;
            }
            case 'after_hooks_check': {
                const hasNewDeath = liveUnitsCanonical(ctx.draft).some((unit) => {
                    const instance = ctx.draft.instances[unit.instanceId];
                    return (instance !== undefined &&
                        effectiveStats(ctx.draft, instance.id).health <= 0);
                });
                if (hasNewDeath) {
                    this.afterDeathsStage = 'end';
                    this.stage = 'deaths';
                }
                else {
                    this.stage = 'end';
                }
                return false;
            }
            case 'end':
                ctx.emit({ type: 'CombatEnded', round: ctx.draft.round });
                this.stage = 'post_end_deaths';
                return false;
            case 'post_end_deaths':
                this.deathWave ??= new DeathWaveMachine(this.registry);
                if (this.deathWave.advance(ctx)) {
                    this.deathWave = null;
                    this.stage = 'done';
                }
                return false;
            case 'done':
                return true;
        }
    }
}
/** Публичный белоящичный вход боя; раунд использует ту же CombatPhaseMachine. */
export function resolveCombat(registry, state, reactionCandidates) {
    const combat = reactionCandidates === undefined
        ? new CombatPhaseMachine(registry)
        : new CombatPhaseMachine(registry, () => reactionCandidates);
    const workflow = { advance: (ctx) => combat.advance(ctx) };
    return startResolutionWorkflow(registry, state, workflow);
}
//# sourceMappingURL=resolveCombat.js.map